import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Banknote,
  CalendarRange,
  CheckCircle2,
  Clock3,
  FileText,
  Landmark,
  RefreshCw,
  Sparkles,
  TriangleAlert,
  WalletCards,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, EmptyState, PageSection } from '@/ui/components';
import { financeService, type FinanceDashboard, type FinancePeriodPreset } from '@/services/financeService';
import './financePortal.css';

function currency(value: number, compact = false) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: compact ? 0 : 2,
    notation: compact && Math.abs(value) >= 1_000_000 ? 'compact' : 'standard',
  }).format(value || 0);
}

function percent(value: number) {
  return `${Number(value || 0).toFixed(value % 1 ? 1 : 0)}%`;
}

function readableError(error: unknown) {
  return error instanceof Error && error.message ? error.message : 'Finance data could not be loaded.';
}

function buildPipelineAxis(maximum: number) {
  const safeMaximum = Math.max(1, maximum);
  const roughStep = safeMaximum / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalizedStep = roughStep / magnitude;
  const stepFactor = normalizedStep <= 1 ? 1
    : normalizedStep <= 2 ? 2
      : normalizedStep <= 2.5 ? 2.5
        : normalizedStep <= 5 ? 5
          : 10;
  const step = stepFactor * magnitude;
  const axisMaximum = Math.ceil(safeMaximum / step) * step;
  const ticks = Array.from(
    { length: Math.round(axisMaximum / step) + 1 },
    (_, index) => index * step,
  );

  return { maximum: axisMaximum, ticks };
}

function thousands(value: number) {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(value / 1_000);
}

const PIPELINE_SERIES = [
  { key: 'quoted', label: 'Quoted', className: 'quoted' },
  { key: 'accepted', label: 'Accepted', className: 'accepted' },
  { key: 'invoiced', label: 'Invoiced', className: 'invoiced' },
  { key: 'collected', label: 'Collected', className: 'collected' },
] as const;

const CATEGORY_COLOURS = [
  '#2251b4',
  '#668bd8',
  '#0f8a63',
  '#c06c16',
  '#7b6cab',
  '#2f7f8f',
  '#8a5f4a',
] as const;

function buildCategoryGradient(rows: FinanceDashboard['revenueByCategory']) {
  const total = rows.reduce((sum, row) => sum + Math.max(0, row.amount), 0);
  if (total <= 0) return 'var(--color-hover)';

  let cursor = 0;
  const stops = rows.map((row, index) => {
    const start = cursor;
    cursor += (Math.max(0, row.amount) / total) * 100;
    return `${CATEGORY_COLOURS[index % CATEGORY_COLOURS.length]} ${start}% ${cursor}%`;
  });

  return `conic-gradient(${stops.join(', ')})`;
}

function MetricCard({
  label,
  value,
  detail,
  tone,
  icon,
}: {
  label: string;
  value: string;
  detail: string;
  tone: 'ink' | 'green' | 'amber' | 'red';
  icon: React.ReactNode;
}) {
  return (
    <article className={`finance-metric finance-metric--${tone}`}>
      <div className="finance-metric__top">
        <span>{label}</span>
        <span className="finance-metric__icon">{icon}</span>
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

export default function FinancePortalPage() {
  const [reportingPeriod, setReportingPeriod] = useState<FinancePeriodPreset>('rolling-12');
  const [dashboard, setDashboard] = useState<FinanceDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDashboard(await financeService.getDashboard(reportingPeriod));
    } catch (loadError) {
      setError(readableError(loadError));
    } finally {
      setLoading(false);
    }
  }, [reportingPeriod]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const pipelineMax = useMemo(() => Math.max(1, ...(dashboard?.monthly.flatMap((row) => (
    PIPELINE_SERIES.map((series) => row[series.key])
  )) || [1])), [dashboard]);
  const pipelineAxis = useMemo(() => buildPipelineAxis(pipelineMax), [pipelineMax]);
  const ageingMax = Math.max(1, ...(dashboard?.ageing.map((row) => row.amount) || [1]));
  const clientMax = Math.max(1, ...(dashboard?.topClients.map((row) => row.amount) || [1]));
  const categoryTotal = dashboard?.revenueByCategory.reduce((sum, row) => sum + row.amount, 0) || 0;

  return (
    <PageSection className="finance-portal" width="wide" padding="compact">
      <section className="finance-hero">
        <div className="finance-hero__copy">
          <span className="finance-eyebrow"><Sparkles size={14} /> Finance intelligence</span>
          <h1>From quote to cash, in one view.</h1>
          <p>
            Live quotation, invoicing, receivables, and payment-ledger signals for {dashboard?.account || 'your finance account'}.
          </p>
          <span className="finance-basis">
            {dashboard ? `${dashboard.period.label} · ${dashboard.amountBasis}` : 'Amounts are presented in AUD.'}
          </span>
        </div>
        <div className="finance-hero__controls">
          <label>
            <CalendarRange size={16} />
            <span className="sr-only">Reporting period</span>
            <select
              value={reportingPeriod}
              onChange={(event) => setReportingPeriod(event.target.value as FinancePeriodPreset)}
            >
              <optgroup label="Rolling calendar periods">
                <option value="rolling-1">Last 1 month</option>
                <option value="rolling-3">Last 3 months</option>
                <option value="rolling-6">Last 6 months</option>
                <option value="rolling-12">Last 12 months</option>
                <option value="rolling-18">Last 18 months</option>
                <option value="rolling-24">Last 24 months</option>
              </optgroup>
              <optgroup label="Financial and calendar years">
                <option value="financial-year-current">This financial year</option>
                <option value="financial-year-last">Last financial year</option>
                <option value="year-to-date">Calendar year to date</option>
              </optgroup>
            </select>
          </label>
          <Button
            type="button"
            variant="secondary"
            loading={loading}
            iconBefore={<RefreshCw size={16} />}
            onClick={() => void loadDashboard()}
          >
            Refresh
          </Button>
          <Link className="finance-reconcile-link" to="/ai?tab=reconcile">
            <Landmark size={16} /> Reconcile payments <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {error ? (
        <div className="finance-alert" role="alert">
          <TriangleAlert size={18} />
          <span>{error}</span>
          <Button type="button" size="sm" variant="secondary" onClick={() => void loadDashboard()}>Try again</Button>
        </div>
      ) : null}

      {!dashboard && loading ? (
        <div className="finance-loading" aria-label="Loading finance dashboard">
          {Array.from({ length: 6 }, (_, index) => <span key={index} />)}
        </div>
      ) : null}

      {dashboard ? (
        <>
          <section className="finance-metrics" aria-label="Finance summary">
            <MetricCard label="Quoted" value={currency(dashboard.totals.quoted, true)} detail={`${dashboard.totals.quoteCount} quotations dated in period`} tone="ink" icon={<FileText size={18} />} />
            <MetricCard label="Accepted" value={currency(dashboard.totals.accepted, true)} detail={`${dashboard.totals.acceptedCount} quotations accepted in period`} tone="green" icon={<CheckCircle2 size={18} />} />
            <MetricCard label="Total outstanding" value={currency(dashboard.totals.outstanding, true)} detail="All open issued invoices in the database" tone="amber" icon={<Clock3 size={18} />} />
            <MetricCard label="Invoiced" value={currency(dashboard.totals.invoiced, true)} detail={`${dashboard.totals.invoiceCount} invoices issued in period`} tone="ink" icon={<WalletCards size={18} />} />
            <MetricCard label="Collected" value={currency(dashboard.totals.collected, true)} detail={`${dashboard.totals.paymentCount} payments in period`} tone="green" icon={<ArrowDownRight size={18} />} />
            <MetricCard label="Current overdue" value={currency(dashboard.totals.overdue, true)} detail={`${dashboard.totals.overdueCount} overdue invoices issued in period`} tone="red" icon={<ArrowUpRight size={18} />} />
          </section>

          <section className="finance-grid finance-grid--primary">
            <article className="finance-panel finance-panel--pipeline">
              <header className="finance-panel__header">
                <div><span className="finance-kicker">Monthly movement</span><h2>Commercial pipeline</h2></div>
                <div className="finance-legend">
                  {PIPELINE_SERIES.map((series) => <span key={series.key}><i className={`finance-dot finance-dot--${series.className}`} />{series.label}</span>)}
                </div>
              </header>
              <div className="finance-pipeline-chart" aria-label="Commercial pipeline by month; vertical axis in thousands of dollars">
                <div className="finance-pipeline-axis" aria-hidden="true">
                  <span className="finance-pipeline-axis__unit">$000</span>
                  <div className="finance-pipeline-axis__scale">
                    {pipelineAxis.ticks.map((tick) => (
                      <span
                        key={tick}
                        style={{ top: `${100 - (tick / pipelineAxis.maximum) * 100}%` }}
                      >
                        {thousands(tick)}
                      </span>
                    ))}
                  </div>
                  <span />
                </div>
                <div className="finance-pipeline-plot">
                  <div className="finance-pipeline-grid" aria-hidden="true">
                    {pipelineAxis.ticks.map((tick) => (
                      <span
                        key={tick}
                        style={{ top: `${100 - (tick / pipelineAxis.maximum) * 100}%` }}
                      />
                    ))}
                  </div>
                  <div className="finance-pipeline-months">
                    {dashboard.monthly.map((month) => (
                      <div className="finance-pipeline-month" key={month.month}>
                        <div className="finance-pipeline-bars">
                          {PIPELINE_SERIES.map((series) => (
                            <span
                              key={series.key}
                              className={`finance-pipeline-bar finance-pipeline-bar--${series.className}`}
                              style={{ height: `${Math.max(month[series.key] > 0 ? 5 : 0, (month[series.key] / pipelineAxis.maximum) * 100)}%` }}
                              title={`${series.label}: ${currency(month[series.key])}`}
                            />
                          ))}
                        </div>
                        <span>{month.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            <article className="finance-panel finance-panel--velocity">
              <header className="finance-panel__header"><div><span className="finance-kicker">Cash velocity</span><h2>Collection health</h2></div></header>
              <div className="finance-velocity-number">
                <strong>{dashboard.totals.averageDaysToPay ?? '—'}</strong>
                <span>average days to pay</span>
              </div>
              <dl className="finance-velocity-list">
                <div><dt>Stripe fees</dt><dd>{currency(dashboard.totals.stripeFees)}</dd></div>
                <div><dt>Refunds</dt><dd>{currency(dashboard.totals.refunds)}</dd></div>
                <div><dt>Ledger payments</dt><dd>{dashboard.dataQuality.actualPaymentCount}</dd></div>
              </dl>
              {dashboard.dataQuality.paymentsAreEstimated ? (
                <p className="finance-data-note"><TriangleAlert size={15} /> {dashboard.dataQuality.estimatedPaymentCount} legacy paid invoice(s) are estimated until reconciled.</p>
              ) : (
                <p className="finance-data-note finance-data-note--good"><CheckCircle2 size={15} /> Collected values are backed by payment transactions.</p>
              )}
            </article>
          </section>

          <section className="finance-grid finance-grid--secondary">
            <article className="finance-panel">
              <header className="finance-panel__header"><div><span className="finance-kicker">Receivables</span><h2>True ageing by due date</h2></div></header>
              <div className="finance-ranked-list">
                {dashboard.ageing.map((row) => (
                  <div className="finance-ranked-row" key={row.key}>
                    <span>{row.label}<small>{row.count} invoice{row.count === 1 ? '' : 's'}</small></span>
                    <div><i style={{ width: `${(row.amount / ageingMax) * 100}%` }} /></div>
                    <strong>{currency(row.amount, true)}</strong>
                  </div>
                ))}
              </div>
            </article>

            <article className="finance-panel">
              <header className="finance-panel__header"><div><span className="finance-kicker">Concentration</span><h2>Top outstanding clients</h2></div></header>
              {dashboard.topClients.length ? (
                <div className="finance-ranked-list finance-ranked-list--clients">
                  {dashboard.topClients.map((row) => (
                    <div className="finance-ranked-row" key={row.client}>
                      <span title={row.client}>{row.client}</span>
                      <div><i style={{ width: `${(row.amount / clientMax) * 100}%` }} /></div>
                      <strong>{currency(row.amount, true)}</strong>
                    </div>
                  ))}
                </div>
              ) : <EmptyState icon={<CheckCircle2 size={26} />} title="No outstanding clients" description="All issued balances are covered." />}
            </article>

            <article className="finance-panel finance-panel--mix">
              <header className="finance-panel__header"><div><span className="finance-kicker">Work mix</span><h2>Invoiced revenue by category</h2></div></header>
              {dashboard.revenueByCategory.length ? (
                <div className="finance-mix-content">
                  <div className="finance-donut-wrap">
                    <div
                      className="finance-donut"
                      role="img"
                      aria-label={`Revenue mix by category. ${dashboard.revenueByCategory.map((row) => `${row.category} ${percent(categoryTotal > 0 ? (row.amount / categoryTotal) * 100 : 0)}`).join(', ')}.`}
                      style={{ background: buildCategoryGradient(dashboard.revenueByCategory) }}
                    >
                      <span className="finance-donut__centre" aria-hidden="true">
                        <strong>{currency(categoryTotal, true)}</strong>
                        <small>invoiced</small>
                      </span>
                    </div>
                  </div>
                  <div className="finance-mix-list">
                    {dashboard.revenueByCategory.map((row, index) => (
                      <div key={row.category}>
                        <i
                          className="finance-mix-swatch"
                          style={{ backgroundColor: CATEGORY_COLOURS[index % CATEGORY_COLOURS.length] }}
                        />
                        <span>{row.category}</span>
                        <strong>{categoryTotal > 0 ? percent((row.amount / categoryTotal) * 100) : '0%'}</strong>
                        <small>{currency(row.amount, true)}</small>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <EmptyState icon={<Banknote size={26} />} title="No category data" description="Issued invoice line categories will appear here." />}
            </article>
          </section>
        </>
      ) : null}
    </PageSection>
  );
}
